import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronRight, MapPin } from 'lucide-react';
import { TYPES_OPERATION, TYPES_BATIMENT, TERRITOIRES } from './constants';

export default function OperationTab({ identite: identite, operation, setOperation, setActiveTab }) {
  return (
    <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-emerald-600" />
                    Territoire et type d'opération
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Territoire DOM *</Label>
                    <Select 
                      value={operation.territoire} 
                      onValueChange={(v) => setOperation(prev => ({ ...prev, territoire: v }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="select-territoire">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TERRITOIRES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            <span className="mr-2">{t.emoji}</span>
                            {t.label} ({t.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Type d'opération *</Label>
                      <Select 
                        value={operation.type_operation} 
                        onValueChange={(v) => setOperation(prev => ({ ...prev, type_operation: v }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TYPES_OPERATION.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Type de bâtiment *</Label>
                      <Select 
                        value={operation.type_batiment} 
                        onValueChange={(v) => setOperation(prev => ({ ...prev, type_batiment: v }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TYPES_BATIMENT.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Localisation et Permis de construire</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Adresse du chantier *</Label>
                    <Input
                      value={operation.adresse_chantier}
                      onChange={(e) => setOperation(prev => ({ ...prev, adresse_chantier: e.target.value }))}
                      className="mt-1"
                      data-testid="input-adresse-chantier"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Code postal *</Label>
                      <Input
                        value={operation.code_postal_chantier}
                        onChange={(e) => setOperation(prev => ({ ...prev, code_postal_chantier: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Ville *</Label>
                      <Input
                        value={operation.ville_chantier}
                        onChange={(e) => setOperation(prev => ({ ...prev, ville_chantier: e.target.value }))}
                        className="mt-1"
                        data-testid="input-ville-chantier"
                      />
                    </div>
                    <div>
                      <Label>Pays</Label>
                      <Input
                        value={operation.pays_chantier}
                        onChange={(e) => setOperation(prev => ({ ...prev, pays_chantier: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Référence cadastrale</Label>
                      <Input
                        value={operation.reference_cadastrale}
                        onChange={(e) => setOperation(prev => ({ ...prev, reference_cadastrale: e.target.value }))}
                        className="mt-1"
                        placeholder="Ex: Section AB, Parcelle 123"
                      />
                    </div>
                    <div>
                      <Label>Destination de l'opération *</Label>
                      <Select 
                        value={operation.destination_operation} 
                        onValueChange={(v) => setOperation(prev => ({ ...prev, destination_operation: v }))}
                      >
                        <SelectTrigger className="mt-1" data-testid="select-destination-operation">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Habitation">Habitation</SelectItem>
                          <SelectItem value="Habitation principale">Habitation principale</SelectItem>
                          <SelectItem value="Habitation secondaire">Habitation secondaire</SelectItem>
                          <SelectItem value="Commercial">Commercial</SelectItem>
                          <SelectItem value="Mixte (Habitation + Commercial)">Mixte (Habitation + Commercial)</SelectItem>
                          <SelectItem value="Industriel">Industriel</SelectItem>
                          <SelectItem value="Agricole">Agricole</SelectItem>
                          <SelectItem value="Bureaux">Bureaux</SelectItem>
                          <SelectItem value="Equipement public">Equipement public</SelectItem>
                          <SelectItem value="Etablissement recevant du public (ERP)">Etablissement recevant du public (ERP)</SelectItem>
                          <SelectItem value="Entrepôt / Logistique">Entrepôt / Logistique</SelectItem>
                          <SelectItem value="Hébergement / Hôtellerie">Hébergement / Hôtellerie</SelectItem>
                          <SelectItem value="Enseignement">Enseignement</SelectItem>
                          <SelectItem value="Santé">Santé</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <Separator className="my-4" />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Référence permis de construire</Label>
                      <Input
                        value={operation.reference_permis}
                        onChange={(e) => setOperation(prev => ({ ...prev, reference_permis: e.target.value }))}
                        className="mt-1"
                        placeholder="Ex: PC 971-2024-00123"
                      />
                    </div>
                    <div>
                      <Label>Délivré par la commune</Label>
                      <Input
                        value={operation.commune_delivrance_permis}
                        onChange={(e) => setOperation(prev => ({ ...prev, commune_delivrance_permis: e.target.value }))}
                        className="mt-1"
                        placeholder="Ex: Basse-Terre"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date du permis</Label>
                      <DatePickerFr
                        className="mt-1 w-full"
                        value={operation.date_permis}
                        onChange={(v) => setOperation(prev => ({ ...prev, date_permis: v }))}
                      />
                    </div>
                    <div>
                      <Label>Coût global (honoraires inclus)</Label>
                      <Input
                        type="number"
                        value={operation.cout_global_ttc}
                        onChange={(e) => setOperation(prev => ({ ...prev, cout_global_ttc: parseFloat(e.target.value) || '' }))}
                        className="mt-1"
                        placeholder="Montant en €"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Description du projet</Label>
                    <Textarea
                      value={operation.description_projet}
                      onChange={(e) => setOperation(prev => ({ ...prev, description_projet: e.target.value }))}
                      className="mt-1"
                      rows={3}
                      placeholder="Décrivez brièvement votre projet..."
                    />
                  </div>

                  <div>
                    <Label>Date du procès-verbal de réception</Label>
                    <DatePickerFr
                      className="mt-1 w-full"
                      value={operation.date_reception_pv || ''}
                      onChange={(v) => setOperation(prev => ({ ...prev, date_reception_pv: v }))}
                      id="input-date-reception-pv"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      À renseigner uniquement si l'opération est déjà livrée (PV de réception signé).
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab('identite')}>
                  Retour
                </Button>
                <Button onClick={() => setActiveTab('intervenants')} className="bg-emerald-600 hover:bg-emerald-700">
                  Suivant : Intervenants
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
    </div>
  );
}
